import { useState, useEffect, useCallback, lazy, Suspense, type ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FirstRunTourGate } from '../tour/yapTour';
import { Layout } from '../components/layout';
import { LoadingScreen, ThemeSync, ContextMenu, CommandRegistrar, KeyboardListener, NotificationHost, ProjectDialogsHost, SettingsHost } from '../components/common';
import { ShortcutEditorHost } from '../components/ui/ShortcutEditorHost';
import { McpBridgeHost } from '../components/mcp/McpBridgeHost';
import { loadBuiltInPlugins } from '../plugins';
import { useAppStore } from '../state/store';
import { useDocumentStore } from '../state/useDocumentStore';
import { guardUnsavedChanges, parkActiveDocumentSnapshot, restoreSessionDocument } from '../utils/ProjectFileIO';
import { prefetchWorkbenchChunks } from '../utils/prefetchWorkbenches';
import { documentWorkspace } from '../workspaces/documentWorkspace';
import { useDocSession } from '../workspaces/sessionStore';
import { DocumentTabArea } from '../workspaces/DocumentTabArea';
import { useDocStripHost } from '../workspaces/docStripHost';
import { startToolSyncBridge } from '../state/toolSyncBridge';
import { startSketchLedgerSync } from '../core/sketch/sceneSync';
import { startSnapSystem } from '../tools/snapping/snapSystemSync';
import { startHistoryEngine } from '../core/geometry/history';
import { startDimAssociation } from '../core/commands/dimAssociation';
import { startLispRuntime } from '../core/lisp';
import { isSettingsWindowDocument } from '@suite/settings';
import { AppDialogHost } from '@/components/AppDialogHost';

// Route-level code splitting: each workbench (and the project picker) is a
// separate chunk loaded on navigation. The scene/rendering tree, three-mesh-bvh
// and the CSG helper live only behind these routes, so boot never pays for
// them. Barrels are bypassed on purpose: `../views` would drag BIMSceneContent
// back into Home's chunk via `export *`.
const BimMode = lazy(() =>
    import('../modules/bim').then(m => ({ default: m.BimMode })));
const Mode2D = lazy(() =>
    import('../modules/cad').then(m => ({ default: m.Mode2D })));
const Mode3D = lazy(() =>
    import('../modules/nurbs').then(m => ({ default: m.Mode3D })));
const SketchMode = lazy(() =>
    import('../modules/sketch').then(m => ({ default: m.SketchMode })));
const Home = lazy(() =>
    import('../views/Home').then(m => ({ default: m.Home })));

// The shared two-column settings surface, opened in its own window
// (?view=settings — settings-window plan).
const SettingsWindowPage = lazy(() =>
    import('../settings/SettingsWindowPage').then(m => ({ default: m.SettingsWindowPage })));

/**
 * Wraps one routed workbench in the suite's shared document workspace: the
 * workbench element is the ACTIVE tab's surface; background documents render
 * a paused card and reopen through the session pipeline (parked snapshot or
 * recents file). Creating or opening a project while tabs are open adds a
 * NEW tab — the outgoing document parks its live state instead of being
 * replaced. Single Project Mode (persisted preference) renders the bare
 * workbench without tab chrome and keeps exactly one document open.
 */
function WorkbenchSurface({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    // Chrome slot (Layout's row under the suite top bar) the document tab
    // strip portals into — read before any early return (hooks order).
    const stripPortalTarget = useDocStripHost();
    const kind = useDocumentStore((s) => s.kind);
    const displayName = useDocumentStore((s) => s.displayName);
    const isDirty = useDocumentStore((s) => s.isDirty);
    const recents = useDocumentStore((s) => s.recents);
    const singleProjectMode = useAppStore((s) => s.singleProjectMode);
    const activeKey = `${kind}:${displayName}`;

    useEffect(() => {
        const recent = recents.find((r) => r.name === displayName && r.kind === kind);
        useDocSession.getState().syncActive({
            key: activeKey,
            name: displayName,
            kind,
            dirty: isDirty,
            recent,
        });
        // Single Project Mode keeps exactly one document in the session:
        // stale background tabs (e.g. from before the toggle) are dropped.
        if (singleProjectMode) {
            const session = useDocSession.getState();
            Object.keys(session.docs).forEach((key) => {
                if (key !== activeKey) session.close(key);
            });
        }
    }, [activeKey, displayName, kind, isDirty, recents, singleProjectMode]);

    const handleActivate = useCallback(async (key: string): Promise<boolean> => {
        const session = useDocSession.getState();
        const entry = session.docs[key];
        if (!entry || key === session.activeKey) return false;
        // The outgoing document parks its live state (snapshot) — switching
        // tabs never prompts and never discards unsaved work.
        await parkActiveDocumentSnapshot();
        const ok = await restoreSessionDocument(entry);
        if (!ok) {
            // Restore failed: snap the strip back to the document that is
            // actually still active so the tab highlight never lies.
            const activeKey = useDocSession.getState().activeKey;
            if (activeKey) documentWorkspace.store.getState().activateTab(activeKey);
        }
        return ok;
    }, []);

    const handleCloseRequest = useCallback(async (key: string) => {
        const entry = useDocSession.getState().docs[key];
        if (!entry) return;
        if (entry.dirty && !(await guardUnsavedChanges())) return;
        const wasActive = key === useDocSession.getState().activeKey;
        useDocSession.getState().close(key);
        if (!wasActive) return;
        // Browsers keep the workspace open after closing a tab: activate the
        // last remaining document; only an empty workspace returns to the
        // picker.
        const remaining = Object.values(useDocSession.getState().docs);
        const next = remaining[remaining.length - 1];
        if (next && (await handleActivate(next.key))) return;
        navigate('/', { replace: true });
    }, [navigate, handleActivate]);

    // Single Project Mode: no tab strip — the active surface fills the area.
    if (singleProjectMode) return <>{children}</>;

    return (
        <DocumentTabArea
            activeKey={activeKey}
            activeSurface={children}
            stripPortalTarget={stripPortalTarget}
            onActivate={(key) => void handleActivate(key)}
            onCloseRequest={(key) => void handleCloseRequest(key)}
        />
    );
}

/**
 * Main App component - Yap
 *
 * This is the root component that sets up routing for different modes:
 * - /bim - BIM mode
 * - /2d - 2D drafting mode
 * - /3d - 3D NURBS modeling mode
 * - /sketch - Sketch-style freeform modeling mode
 */
function App() {
    const [isAppReady, setIsAppReady] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Apply the persisted "Default Mode" preference. On a fresh install (no
    // saved settings yet) the project picker at '/' stays; once the user has
    // touched settings at least once, '/' opens the configured workspace.
    // The settings window also boots on '/' — never redirect it (it would
    // drop the ?view=settings parameter).
    useEffect(() => {
        if (isSettingsWindowDocument()) return;
        if (location.pathname !== '/') return;
        let saved: string | null = null;
        try {
            saved = localStorage.getItem('yap-app-settings');
        } catch {
            saved = null;
        }
        if (saved) {
            // 'last' reopens the workbench the app was closed in; a pinned
            // mode keeps its explicit choice. Anything unrecognized (e.g. a
            // persisted pre-removal 'unified' default) falls back to BIM.
            const VALID_MODES = ['bim', '2d', '3d', 'sketch'];
            const { defaultMode, lastMode } = useAppStore.getState();
            const target = defaultMode === 'last'
                ? (VALID_MODES.includes(lastMode) ? lastMode : 'bim')
                : (VALID_MODES.includes(defaultMode) ? defaultMode : 'bim');
            navigate(`/${target}`, { replace: true });
        }
        // Intentionally mount-only: later navigation is user-driven.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Register built-in plugins once on startup
    useEffect(() => {
        loadBuiltInPlugins();
        // Single source of truth: app-store activeTool mirrored into the
        // BIM store so the context menu / OptionsBar fallback never drift.
        startToolSyncBridge();
        // Live OSNAP settings mirror + CAD entity ledger snap registration.
        startSnapSystem();
        // Sketch solids → cross-workbench scene ledger mirror.
        startSketchLedgerSync();
        // Record-History recompute engine (parametric HISTORY) + associative
        // dimension recompute (DIMREASSOCIATE): entity watchers.
        startHistoryEngine();
        startDimAssociation();
        // AutoLISP engine: c: command bridge, Escape-cancellation and the
        // persisted startup suite (docs/plans/2d/autocad-pgp-lisp-plan.md).
        startLispRuntime();
        // Warm the lazy workbench chunks at idle: mode switches then resolve
        // from the module cache instead of fetching behind the loading
        // fallback (the settings window mounts no workbench).
        if (!isSettingsWindowDocument()) prefetchWorkbenchChunks();
    }, []);

    // Keep the shared StatusBar's mode indicator truthful: the app-store
    // `mode` slice is only written by project import otherwise, so route
    // navigation must mirror into it too.
    useEffect(() => {
        const route = location.pathname.replace('/', '');
        if (location.pathname === '/') return;
        const valid = ['bim', '2d', '3d', 'sketch'].includes(route);
        if (valid && useAppStore.getState().mode !== route) {
            useAppStore.getState().setMode(route as any);
        }
    }, [location.pathname]);

    // Ready gate: flip on the next macrotask. The old gate parked boot for a
    // fixed 600ms that nothing downstream needed — the Suspense fallback below
    // already covers the first (idle-prefetched) workbench chunk load.
    useEffect(() => {
        const timer = window.setTimeout(() => setIsAppReady(true), 0);
        return () => window.clearTimeout(timer);
    }, []);

    // The settings window boots this same SPA with ?view=settings: a bare
    // two-column settings surface — no workspace chrome (settings-window
    // plan). It needs no workbench, so it renders before the ready gate.
    if (isSettingsWindowDocument()) {
        return (
            <Suspense fallback={null}>
                <SettingsWindowPage />
                {/* Every app document shows its own dialogs — never the browser's. */}
                <AppDialogHost />
            </Suspense>
        );
    }

    if (!isAppReady) {
        return <LoadingScreen progress={70} message={t('app.preparingWorkspace')} />;
    }

    return (
        <Box h="100vh" w="full" overflow="hidden">
            <ThemeSync />
            <CommandRegistrar />
            <KeyboardListener />
            <NotificationHost />
            <ProjectDialogsHost />
            <SettingsHost />
            <ContextMenu />
            <ShortcutEditorHost />
            <McpBridgeHost />
            {/* First-run tour: auto-starts once (docs/product/onboarding-tour). */}
            <FirstRunTourGate />
            <Layout>
                <Suspense fallback={<LoadingScreen progress={40} message={t('app.loadingWorkspace')} />}>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        {/* Legacy routes: the Unified workbench was removed and
                            the mode addresses were renamed to neutral ids. */}
                        <Route path="/unified" element={<Navigate to="/bim" replace />} />
                        <Route path="/revit" element={<Navigate to="/bim" replace />} />
                        <Route path="/autocad" element={<Navigate to="/2d" replace />} />
                        <Route path="/rhino" element={<Navigate to="/3d" replace />} />
                        <Route path="/bim" element={<WorkbenchSurface><BimMode /></WorkbenchSurface>} />
                        <Route path="/2d" element={<WorkbenchSurface><Mode2D /></WorkbenchSurface>} />
                        <Route path="/3d" element={<WorkbenchSurface><Mode3D /></WorkbenchSurface>} />
                        <Route path="/sketch" element={<WorkbenchSurface><SketchMode /></WorkbenchSurface>} />
                    </Routes>
                </Suspense>
            </Layout>
            {/* Global app dialog host — the single replacement for native browser
                alert/confirm/prompt (shared-components plan). */}
            <AppDialogHost />
        </Box>
    );
}

export default App;
